import React, { useState } from 'react';
import { Card, CardContent, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { setBillingAccountFreeFlag } from '../../Services/billingAccount';

interface Props {
	billingAccount: any;
	onChanged?: () => void;
}

// BillingAccountFreeFlag marque un compte comme GRATUIT : démonstration ou essai
// interne, créé par Quanti et qui ne paie pas. Rien ne distinguait jusqu'ici un
// compte de démo d'un vrai client une fois créé : au nettoyage, il fallait s'en
// remettre à la mémoire de qui l'avait créé.
//
// ⚠️ « Free » désigne CETTE case, et plus la nature calculée « ni contrat ni
// abonnement payant » — celle-ci est devenue l'état « sans abonnement ». Depuis
// l'abandon de l'offre gratuite, un compte n'est gratuit que si on l'a déclaré.
//
// ⚠️ Déclaratif, et rien d'autre : ce drapeau n'ouvre ni ne ferme aucun droit et
// ne change rien à la facturation. Il désigne des candidats au nettoyage, il ne
// décide pas — il est saisi à la main, donc faillible.
export const BillingAccountFreeFlag: React.FC<Props> = ({ billingAccount, onChanged }) => {
	const notify = useNotify();
	const [checked, setChecked] = useState<boolean>(!!billingAccount.isFree);
	const [saving, setSaving] = useState(false);

	const handleToggle = async (next: boolean) => {
		// Coché tout de suite : l'appel est court, et une case qui ne bouge qu'au
		// retour du serveur donne l'impression de ne pas avoir été cliquée.
		setChecked(next);
		setSaving(true);
		try {
			await setBillingAccountFreeFlag(String(billingAccount.id), next);
			notify(next ? 'Marqué comme compte gratuit' : 'Marque « gratuit » retirée', {
				type: 'success',
			});
			onChanged?.();
		} catch (err: any) {
			setChecked(!next); // l'écriture a échoué : la case doit revenir en arrière
			notify(err?.message || 'Échec de la mise à jour', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card style={{ width: '100%', marginTop: 16 }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Nature du compte
				</Typography>
				<FormControlLabel
					control={
						<Checkbox
							checked={checked}
							disabled={saving}
							onChange={(e) => handleToggle(e.target.checked)}
						/>
					}
					label="Compte gratuit (Free)"
				/>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
					Démo ou essai interne, créé par Quanti — pas un client, il ne paie pas.
					Sans effet sur les droits ni sur la facturation : sert à retrouver ces
					comptes, en particulier au moment d'un nettoyage.
				</Typography>
			</CardContent>
		</Card>
	);
};
